import { createShapeNode, type HandleOffsets } from "./factory";
import { handleOffsetsFor, shapeVariants } from "../shapeGeometry";

/**
 * The six shape nodes.  Everything that varies between them is data: a class suffix, a viewBox,
 * an SVG body and (for the shapes whose sides are inset from the node's bounding box) a set of
 * per-side handle offsets.
 *
 * `Group` is deliberately not built here — it has no shape, no fill gradient and uses
 * `.group-heading` rather than `.node-content`, so it stays hand-written in `Group.tsx`.
 */

/**
 * Handle offsets per shape, derived from the shared inset table so the handles and the routed
 * edge endpoints cannot drift apart.  Previously these percentages lived here as inline styles
 * only, which is why the layout engine -- routing to the node's box -- left arrowheads floating
 * beside the pointed shapes.
 */
const handleOffsets: Record<string, HandleOffsets> = Object.fromEntries(
    shapeVariants().map(variant => [variant, handleOffsetsFor(variant)])
);

/** Every shape takes its offsets from the table; the flat-sided ones simply resolve to `{}`. */
const shape = (config: Omit<Parameters<typeof createShapeNode>[0], "handleOffsets">) =>
    createShapeNode({ ...config, handleOffsets: handleOffsets[config.variant] });

export const Rectangle = shape({
    variant: "rectangle",
    viewBox: "0 0 100 60",
    shape: paint => <path d="M1,1 L99,1 L99,59 L1,59 Z" strokeWidth="2" style={paint}></path>,
});

export const RoundedRectangle = shape({
    variant: "rounded-rectangle",
    viewBox: "0 0 100 60",
    shape: paint => <rect x="1" y="1" width="98" height="58" rx="30" ry="50" strokeWidth="2" style={paint} />,
});

export const Chevron = shape({
    variant: "chevron",
    viewBox: "0 0 100 60",
    shape: paint => <path d="M1,1 L20,30 L1,59 L80,59 L99,30 L80,1 Z" strokeWidth="2" style={paint}></path>,
});

export const Diamond = shape({
    variant: "diamond",
    viewBox: "0 0 100 100",
    shape: paint => <path d="M1,50 L50,1 L99,50 L50,99 Z" strokeWidth="2" style={paint}></path>,
});

export const Triangle = shape({
    variant: "triangle",
    viewBox: "0 0 100 80",
    shape: paint => <path d="M1,79 L50,1 L99,79 Z" strokeWidth="2" style={paint}></path>,
});

export const MultiTriangle = shape({
    variant: "multi-triangle",
    viewBox: "0 0 140 90",
    shape: paint => <>
        <polygon points="50 1 1 80 100 80" strokeWidth="2" style={paint}></polygon>
        <polygon points="90 1 40 80 139 80" strokeWidth="2" style={paint}></polygon>
        <polygon points="70 10 20 89 120 89" strokeWidth="2" style={paint}></polygon>
    </>,
});

/**
 * Exported for `nodeTypes.test.tsx` only.  Not published: `library_build` sets `stripInternal`, so
 * this statement is dropped from the emitted `.d.ts` -- see the note at the foot of `layout.ts`.
 *
 * @internal
 */
export { handleOffsets };
